/* eslint-disable max-len */
import { SmartModuleType, } from '.';
export var MetricValueType;
(function (MetricValueType) {
    MetricValueType["NUMBER"] = "number";
    MetricValueType["STRING"] = "string";
})(MetricValueType || (MetricValueType = {}));
/**
 * Type guard function to check if an object is a MetricInfo
 */
export const isMetricInfo = (obj) => {
    const object = obj;
    return (typeof object.name === 'string')
        && (!object.displayName || typeof object.displayName === 'string');
};
export var EvaluationPeriod;
(function (EvaluationPeriod) {
    EvaluationPeriod["DAILY"] = "daily";
    EvaluationPeriod["WEEKLY"] = "weekly";
    EvaluationPeriod["MONTHLY"] = "monthly";
    EvaluationPeriod["YEARLY"] = "yearly";
})(EvaluationPeriod || (EvaluationPeriod = {}));
/**
 * The Operator defines how to compare an aggregated value against threshold(s)
 *      - 'lessThan'(<), 'lessThanOrEqualTo'(<=), 'greaterThanOrEqualTo'(=>), or 'greaterThan'(>) against a threshold value
 *      - 'in', 'notIn' for a value is in or not in the thresholds, or 'between' (greater than or equal to Thresholds[0] AND less than or equal
 *          Thresholds[1]), or 'outside' (negation of 'between', less than Thresholds[0] OR greater than Thresholds[1]).
 * The Thresholds define in-alert threshold(s) for the period. 'between' and 'outside' operators require 2 thresholds. 'equal' and 'notEqual'
 * operators require 1 (or more) threshold(s). Other operators accept only 1 threshold.
 */
export var ThresholdComparisonOperator;
(function (ThresholdComparisonOperator) {
    // The following 4 operator requires EXACTLY 1 threshold
    ThresholdComparisonOperator["LESS_THAN"] = "lessThan";
    ThresholdComparisonOperator["LESS_THAN_OR_EQUAL_TO"] = "lessThanOrEqualTo";
    ThresholdComparisonOperator["GREATER_THAN"] = "greaterThan";
    ThresholdComparisonOperator["GREATER_THAN_OR_EQUAL_TO"] = "greaterThanOrEqualTo";
    // The value must be equal to all thresholds provided. This require minimum 1 threshold and no limit on maximum number of threshold.
    ThresholdComparisonOperator["IN"] = "in";
    // The value must NOT be equal to any thresholds provided. This require minimum 1 threshold and no limit on maximum number of threshold.
    ThresholdComparisonOperator["NOT_IN"] = "notIn";
    // greater than or equal to Thresholds[0] AND less than or equal Thresholds[1]. This operator require 2 thresholds.
    ThresholdComparisonOperator["BETWEEN"] = "between";
    // opposite of 'between', less than Thresholds[0] OR greater than Thresholds[1]. This operator require 2 thresholds.
    ThresholdComparisonOperator["OUTSIDE"] = "outside";
})(ThresholdComparisonOperator || (ThresholdComparisonOperator = {}));
/**
 * Type guard function to check if an object is a NumericMetric's range
 */
export const isNumericMetricRange = (obj) => {
    const object = obj;
    return object !== undefined
        && (object.min === undefined || typeof object.min === 'number')
        && (object.max === undefined || typeof object.max === 'number');
};
/**
 * Type guard function to check if an object is a NumericMetricInfo
 */
export const isNumericMetricInfo = (obj) => {
    const object = obj;
    return isMetricInfo(object)
        && (!object.unit || typeof object.unit === 'string')
        && (!object.range || isNumericMetricRange(object.range));
};
/**
 * Type guard function to check if an object is a TagMetricEnum
 */
export const isTagMetricEnum = (obj) => {
    const object = obj;
    return (typeof object.value === 'string')
        && (typeof object.name === 'string');
};
/**
 * Type guard function to check if an object is a TagMetricInfo
 */
export const isTagMetricInfo = (obj) => {
    const object = obj;
    return isMetricInfo(object)
        && (!object.enumDefinitions || object.enumDefinitions.find((mapValue) => {
            // find a mapValue that is not type TagMetricEnum
            return !isTagMetricEnum(mapValue);
        }) === undefined);
};
export var MetricType;
(function (MetricType) {
    MetricType["RAW"] = "raw";
    MetricType["DERIVED"] = "derived";
})(MetricType || (MetricType = {}));
/**
 * Module types other then SmartModuleType that are also supported by entities
 */
export var EntityModuleType;
(function (EntityModuleType) {
    EntityModuleType["HOME_APP_PROXY"] = "home_app_proxy";
    EntityModuleType["VIDEO"] = "video";
})(EntityModuleType || (EntityModuleType = {}));
/**
 * Type guard function to check if an object is a EntityDataStoreConfiguration
 */
export const isEntityDataStoreConfiguration = (obj) => {
    const object = obj;
    return [...Object.values(SmartModuleType), ...Object.values(EntityModuleType)].includes(object.moduleType)
        && (typeof object.moduleInstanceId === 'string' && object.moduleInstanceId.length > 0)
        && (typeof object.bulkDataLabel === 'string' && object.bulkDataLabel.length > 0);
};
/**
 * Type guard function to check if an object is a EntityMetricsDefinitionLinkTo
 */
export const isEntityMetricsDefinitionLinkTo = (obj) => {
    const object = obj;
    return (typeof object.blobDefinitionId === 'string' && object.blobDefinitionId.length > 0)
        && (typeof object.keyField === 'string' && object.keyField.length > 0);
};
/**
 * Type guard function to check if an object is a EntityMetricsDefinition
 */
export const isEntityMetricsDefinition = (obj) => {
    const object = obj;
    return (typeof object.metricsDefinitionId === 'string' && object.metricsDefinitionId.length > 0)
        && Object.values(MetricType).includes(object.metricsType)
        && (!object.numericMetrics || (object.numericMetrics && !object.numericMetrics.find((metric) => {
            return !isNumericMetricInfo(metric);
        }) === undefined))
        && (!object.tagMetrics || (object.tagMetrics && !object.tagMetrics.find((metric) => {
            return !isTagMetricInfo(metric);
        }) === undefined))
        && isEntityDataStoreConfiguration(object.dataStoreConfiguration)
        && (!object.linksTo || (object.linksTo instanceof Array && object.linksTo.every((linkTo) => {
            return isEntityMetricsDefinitionLinkTo(linkTo);
        })));
};
/**
 * Type guard function to check if an object is a EntityBlobDefinition
 */
export const isEntityBlobDefinition = (obj) => {
    const object = obj;
    return (typeof object.blobDefinitionId === 'string' && object.blobDefinitionId.length > 0)
        && (!object.displayName || (typeof object.displayName === 'string' && object.blobDefinitionId.length > 0))
        && isEntityDataStoreConfiguration(object.dataStoreConfiguration);
};
export const isS3FileInfo = (obj) => {
    const object = obj;
    return (typeof object.fileName === 'string' && object.fileName.length > 0)
        && (typeof object.url === 'string' && object.url.length > 0);
};
/**
 * Type guard function to check if an object is a EntityDisplaySettings
 */
export const isEntityDisplaySettings = (obj) => {
    const object = obj;
    return (typeof object.name === 'string' && object.name.length > 0)
        && (!object.description || typeof object.description === 'string')
        && (object.iconData === undefined || isS3FileInfo(object.iconData))
        && (object.imageData === undefined || isS3FileInfo(object.imageData))
        && (!object.uiSettings || typeof object.uiSettings === 'object');
};
export var AttributeValueCompareOperator;
(function (AttributeValueCompareOperator) {
    AttributeValueCompareOperator["LESS_THAN"] = "<";
    AttributeValueCompareOperator["LESS_THAN_OR_EQUAL"] = "<=";
    AttributeValueCompareOperator["EQUAL"] = "==";
    AttributeValueCompareOperator["EXACT_EQUAL"] = "===";
    AttributeValueCompareOperator["GREATER_THAN_OR_EQUAL"] = ">=";
    AttributeValueCompareOperator["GREATER_THAN"] = ">";
    AttributeValueCompareOperator["INCLUDE"] = "~=";
    AttributeValueCompareOperator["EXACT_INCLUDE"] = "~==";
})(AttributeValueCompareOperator || (AttributeValueCompareOperator = {}));
export var AttributeConditionConjunction;
(function (AttributeConditionConjunction) {
    AttributeConditionConjunction["AND"] = "AND";
    AttributeConditionConjunction["OR"] = "OR";
})(AttributeConditionConjunction || (AttributeConditionConjunction = {}));
export var EntityAttributeType;
(function (EntityAttributeType) {
    EntityAttributeType["STRING"] = "string";
    EntityAttributeType["NUMBER"] = "number";
    EntityAttributeType["BOOLEAN"] = "boolean";
    EntityAttributeType["ARRAY"] = "array";
    EntityAttributeType["OBJECT"] = "object";
})(EntityAttributeType || (EntityAttributeType = {}));
/**
 * Type guard function to check if an object is a EntityAttributeParam
 */
export const isEntityAttributeParam = (obj) => {
    const object = obj;
    return (object !== undefined && typeof object === 'object')
        && (typeof object.key === 'string' && object.key.length > 0)
        && (object.type !== undefined && Object.values(EntityAttributeType).includes(object.type))
        && (typeof object.label === 'string' && object.label.length > 0)
        && (!object.description || typeof object.description === 'string');
};
/**
 * Type guard function to check if an object is a EntityClass
 */
export const isEntityClass = (obj) => {
    const object = obj;
    return (typeof object.entityClass === 'string' && object.entityClass.length > 0)
        && (typeof object.projectId === 'number')
        && (isEntityDisplaySettings(object.displaySettings))
        && (object.metricsDefinitions === undefined || object.metricsDefinitions.every((metricDef) => {
            return isEntityMetricsDefinition(metricDef);
        }))
        && (object.attributeSchema === undefined || isEntityAttributeParam(object.attributeSchema));
};
/**
 * Type guard function to check if an object is a Entity
 */
export const isEntity = (obj) => {
    const object = obj;
    return (typeof object.entityId === 'string' && object.entityId.length > 0)
        && (typeof object.projectId === 'number')
        && (typeof object.homeId === 'number')
        && (typeof object.entityClass === 'string' && object.entityClass.length > 0)
        && (!object.parentId || (typeof object.parentId === 'string' && object.parentId.length > 0))
        && (typeof object.timeZone === 'string' && object.timeZone.length > 0)
        && (isEntityDisplaySettings(object.displaySettings));
};
export var EntitySortFieldType;
(function (EntitySortFieldType) {
    EntitySortFieldType["BASIC"] = "basic";
    EntitySortFieldType["ATTRIBUTES"] = "attributes";
    EntitySortFieldType["DESCENDANT_COUNTS"] = "descendantCounts";
})(EntitySortFieldType || (EntitySortFieldType = {}));
